import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { AccountBookmarkModel, BOOKMARK_TYPE } from '@/lib/models';
import { accountService } from '@/lib/services';
import { useAccount, useHandleError } from '@/hooks';
import { LinkBox, LoadingButton, RecentlyViewed, ResearchListItem } from '@/components';

export const Bookmarks = () => {
	const { account } = useAccount();
	const handleError = useHandleError();

	const pageRef = useRef(0);
	const sizeRef = useRef(10);

	const [bookmarks, setBookmarks] = useState<AccountBookmarkModel[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);

	const fetchBookmarks = useCallback(async () => {
		setIsLoading(true);

		try {
			if (!account) {
				throw new Error('account is undefined.');
			}

			const response = await accountService
				.getAccountBookmarks(account.accountId, {
					page: pageRef.current,
					size: sizeRef.current,
				})
				.fetch();

			setTotalCount(response.totalCount);
			setBookmarks((previousValue) => {
				if (pageRef.current === 0) {
					return response.bookmarks;
				}

				return previousValue.concat(response.bookmarks);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [account, handleError]);

	useEffect(() => {
		fetchBookmarks();
	}, [fetchBookmarks]);

	const handleBookmarkItemChange = (bookmark: AccountBookmarkModel) => {
		setBookmarks((previousValue) => previousValue.filter((b) => b.bookmarkId !== bookmark.bookmarkId));
		setTotalCount((previousValue) => previousValue - 1);
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		fetchBookmarks();
	};

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Bookmarks</title>
			</Helmet>

			<Container className="pt-7 pb-16">
				<Row className="pb-10">
					<Col>
						<h2 className="mb-0 text-primary">
							Your {totalCount} Bookmark{totalCount === 1 ? '' : 's'}
						</h2>
					</Col>
				</Row>
				<Row>
					<Col xs={12} xl={8} className="mb-6">
						{bookmarks.length === 0 ? (
							<p className="mb-2 text-muted text-center">
								<strong>No Results</strong>
							</p>
						) : (
							<>
								{bookmarks.map(({ bookmarkId, bookmarkContent }) => (
									<ResearchListItem
										key={bookmarkId}
										documentId={bookmarkContent.documentId}
										category={bookmarkContent.documentTypeName}
										fileType={bookmarkContent.fileType ?? undefined}
										viewUrl={bookmarkContent.viewUrl ?? ''}
										shareUrl={bookmarkContent.relativeUrl}
										downloadUrl={bookmarkContent.downloadUrl ?? ''}
										canDownload={bookmarkContent.canDownload}
										bookmarkType={BOOKMARK_TYPE.DOCUMENT}
										isBookmarked={bookmarkContent.bookmarkedFlag}
										to={bookmarkContent.relativeUrl}
										title={bookmarkContent.name}
										description={bookmarkContent.listDescription ?? ''}
										imageSrc={bookmarkContent.imageUrl ?? ''}
										date={bookmarkContent.publicationDateDescription}
										tags={bookmarkContent.topicTags.map((tag) => tag.name)}
										onChange={handleBookmarkItemChange}
										isPodcast={!!bookmarkContent.podcastUrl}
									/>
								))}
								{bookmarks.length < totalCount && (
									<LoadingButton
										isLoading={isLoading}
										variant="outline-primary"
										onClick={handleShowMoreButtonClick}
									>
										Show 10 More
									</LoadingButton>
								)}
							</>
						)}
					</Col>
					<Col xs={12} xl={4}>
						<RecentlyViewed className="mb-2" />
						<LinkBox
							sections={[
								{
									title: "Can't find what you're looking for?",
									links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
								},
							]}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
